import React, { useRef, useState, useEffect } from "react";
import { KeyIcon, CameraIcon } from "@heroicons/react/24/outline";
import { UserIcon } from "@heroicons/react/24/solid";
import { authAPI } from "../../../api/authApi";
import { useToast } from "../../../hooks/useToast";
import Spinner from "../../../ui/Spinner";
import { useAuth } from "../../../contexts/AuthContext";
import AvatarCropModal from "../modals/AvatarCropModal";

export default function ProfileCard({ form, onChange, onShowChangePassword }) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [cropUrl, setCropUrl] = useState(null);
  const { toast } = useToast();
  const { syncUser } = useAuth();

  // Revoke cropUrl on unmount to prevent memory leak if user navigates away
  const cropUrlRef = useRef(null);
  cropUrlRef.current = cropUrl;
  useEffect(() => {
    return () => {
      if (cropUrlRef.current) URL.revokeObjectURL(cropUrlRef.current);
    };
  }, []);

  // File selected → open crop modal (no upload yet)
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCropUrl(URL.createObjectURL(file));
    e.target.value = ""; // reset so re-selecting the same file fires onChange again
  };

  // User cancelled the crop modal
  const handleCropCancel = () => {
    if (cropUrl) URL.revokeObjectURL(cropUrl);
    setCropUrl(null);
  };

  // User saved the crop → upload the cropped blob
  const handleCropSave = async (croppedBlob) => {
    // Close modal first
    if (cropUrl) URL.revokeObjectURL(cropUrl);
    setCropUrl(null);

    // Optimistic preview from the cropped blob
    const previewUrl = URL.createObjectURL(croppedBlob);
    const previousUrl = form.avatarUrl;
    onChange("avatarUrl", previewUrl);
    setUploading(true);

    try {
      const response = await authAPI.uploadAvatar(croppedBlob);
      onChange("avatarUrl", response.data.photoUrl);
      syncUser({ avatar: response.data.photoUrl });
    } catch {
      onChange("avatarUrl", previousUrl);
      toast.error("Failed to upload avatar", 3000);
    } finally {
      URL.revokeObjectURL(previewUrl);
      setUploading(false);
    }
  };

  return (
    <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-4 p-4 rounded-xl border border-border bg-surface-elevated">
      {/* Avatar + Name/Email row */}
      <div className="flex items-center gap-4">
        <div className="h-14 w-14 rounded-full overflow-hidden bg-surface-sunken border border-border flex-shrink-0 relative">
          {form.avatarUrl ? (
            <img
              src={form.avatarUrl}
              alt="Avatar"
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="h-full w-full grid place-items-center bg-surface-muted dark:bg-surface-darker text-text-muted">
              <UserIcon className="h-7 w-7" />
            </div>
          )}
          {uploading && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-full">
              <Spinner size={20} className="text-white" />
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-text-primary truncate">
            {form.displayName}
          </div>
          <div className="text-xs text-text-muted truncate">{form.email}</div>
        </div>
      </div>

      {/* Buttons - side by side on all screens */}
      <div className="flex items-center gap-2 sm:ml-auto">
        <button
          type="button"
          disabled={uploading}
          onClick={() => fileInputRef.current?.click()}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-surface-elevated border border-border-strong text-xs hover:bg-surface disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <CameraIcon className="h-4 w-4" />
          {uploading ? "Uploading..." : "Change photo"}
        </button>
        <button
          type="button"
          onClick={onShowChangePassword}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-surface-elevated border border-border-strong text-xs hover:bg-surface"
        >
          <KeyIcon className="h-4 w-4" /> Change password
        </button>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
      />

      {/* Crop modal — rendered here, portals to document.body */}
      {cropUrl && (
        <AvatarCropModal
          imageUrl={cropUrl}
          onSave={handleCropSave}
          onClose={handleCropCancel}
        />
      )}
    </div>
  );
}
